/**
 * Array Field Component
 * 
 * Renders an array input with add/remove functionality
 */

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import type { InferredPropertySchema } from '@/types/inferred-schema';

interface ArrayFieldProps {
  property: InferredPropertySchema;
  value: any[];
  onChange: (value: any[]) => void;
  error?: string;
}

export function ArrayField({ property, value, onChange, error }: ArrayFieldProps) {
  const items = value || [];

  const handleAdd = () => {
    onChange([...items, '']);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, newValue: string) => {
    const newItems = [...items];
    newItems[index] = newValue;
    onChange(newItems);
  };

  return (
    <div className="space-y-2">
      <Label>
        {property.label}
        {property.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {property.description && (
        <p className="text-sm text-muted-foreground">{property.description}</p>
      )}
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex gap-2">
            <Input
              value={item}
              onChange={(e) => handleChange(index, e.target.value)}
              placeholder={property.placeholder || `Item ${index + 1}`}
              className={error ? 'border-destructive' : ''}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => handleRemove(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {property.helpText && !error && (
        <p className="text-xs text-muted-foreground">{property.helpText}</p>
      )}
    </div>
  );
}
